import React, { useState, useEffect } from 'react'
import { Check, ChevronLeft, ChevronRight } from 'lucide-react'
import EventDetailsStep from './wizard/EventDetailsStep'
import MenuProductsStep from './wizard/MenuProductsStep'
import LocationScheduleStep from './wizard/LocationScheduleStep'
import RulesSettingsStep from './wizard/RulesSettingsStep'
import ReviewPublishStep from './wizard/ReviewPublishStep'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../ui/EnterpriseToast'

export interface MenuItem {
  id: string
  name: string
  price: number
  description?: string
  image_url?: string
  inventory: number | null // null = unlimited
  lobster_tail_qty: number // NEW - Phase 2: Number of lobster tails in this item
  variants?: MenuItemVariant[]
  order_index: number
}

export interface MenuItemVariant {
  id: string
  name: string // e.g., "Size", "Spice Level"
  options: VariantOption[]
}

export interface VariantOption {
  id: string
  value: string // e.g., "8oz", "Medium"
  price_adjustment: number // e.g., +2.00, -1.00
}

export interface EventFormData {
  // Step 1: Event Details
  title: string
  description: string
  image_url?: string

  // Step 2: Menu & Products
  menuItems: MenuItem[]

  // Step 3: Location & Schedule
  location_name: string
  address: string
  zip_code: string
  latitude: number | null
  longitude: number | null
  event_date: string
  notification_date: string
  registration_opens: string
  registration_closes: string
  pickup_start_time: string
  pickup_end_time: string

  // Step 4: Rules & Settings
  status: 'draft' | 'scheduled'
  max_orders: number | null
  pre_order_limit: number | null
  max_lobster_tails: number | null // NEW - Phase 2: Total tail capacity for event
  max_tails_per_order: number | null // NEW - Phase 2: Per-customer tail limit
}

interface EventCreationWizardProps {
  onSuccess?: (event: any) => void
  onCancel?: () => void
  draftId?: string // For editing existing drafts
}

const STEPS = [
  { id: 1, name: 'Event Details', component: EventDetailsStep },
  { id: 2, name: 'Menu & Products', component: MenuProductsStep },
  { id: 3, name: 'Location & Schedule', component: LocationScheduleStep },
  { id: 4, name: 'Rules & Settings', component: RulesSettingsStep },
  { id: 5, name: 'Review & Publish', component: ReviewPublishStep }
]

const EventCreationWizard: React.FC<EventCreationWizardProps> = ({
  onSuccess,
  onCancel,
  draftId
}) => {
  const { user } = useAuth()
  const [currentStep, setCurrentStep] = useState(1)
  const [completedSteps, setCompletedSteps] = useState<number[]>([])
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    image_url: undefined,
    menuItems: [],
    location_name: '',
    address: '',
    zip_code: '',
    latitude: null,
    longitude: null,
    event_date: '',
    notification_date: '',
    registration_opens: '',
    registration_closes: '',
    pickup_start_time: '',
    pickup_end_time: '',
    status: 'draft',
    max_orders: null,
    pre_order_limit: null,
    max_lobster_tails: null,
    max_tails_per_order: null
  })

  // Function to clear localStorage draft
  const clearDraft = () => {
    const confirmed = window.confirm(
      'Clear Draft?\n\n' +
      'This will permanently delete your current draft and start fresh.\n\n' +
      'This action cannot be undone.'
    )

    if (confirmed) {
      localStorage.removeItem('event_draft')
      // Reset to initial state
      setFormData({
        title: '',
        description: '',
        image_url: undefined,
        menuItems: [],
        location_name: '',
        address: '',
        zip_code: '',
        latitude: null,
        longitude: null,
        event_date: '',
        notification_date: '',
        registration_opens: '',
        registration_closes: '',
        pickup_start_time: '',
        pickup_end_time: '',
        status: 'draft',
        max_orders: null,
        pre_order_limit: null,
        max_lobster_tails: null,
        max_tails_per_order: null
      })
      setCurrentStep(1)
      setCompletedSteps([])
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Draft Cleared"
          message="Starting fresh with a new event"
        />
      ))
    }
  }

  // Helper to migrate old date format to new datetime format
  const migrateDateFields = (data: EventFormData): EventFormData => {
    const migrateDate = (dateStr: string | undefined, defaultTime = '12:00'): string => {
      if (!dateStr) return ''
      // If already ISO format, return as-is
      if (dateStr.includes('T')) return dateStr
      // If YYYY-MM-DD format, convert to ISO datetime at specified time
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return new Date(`${dateStr}T${defaultTime}`).toISOString()
      }
      return dateStr
    }

    return {
      ...data,
      notification_date: migrateDate(data.notification_date),
      event_date: migrateDate(data.event_date, '18:00'), // Default to 6pm for events
    }
  }

  // Load draft if editing
  useEffect(() => {
    if (draftId) {
      loadDraft(draftId)
    } else {
      // Load from localStorage if available (auto-save recovery)
      const saved = localStorage.getItem('event_draft')
      if (saved) {
        try {
          const parsed = JSON.parse(saved)

          // Show notification that draft was recovered
          const savedDate = parsed.savedAt ? new Date(parsed.savedAt).toLocaleString() : 'Unknown time'
          const shouldLoad = window.confirm(
            'Recover Previous Draft?\n\n' +
            `A draft was found from ${savedDate}.\n\n` +
            'Would you like to continue editing it?\n\n' +
            'Click "Cancel" to start fresh.'
          )

          if (shouldLoad) {
            // Migrate old date format to new datetime format
            const migratedData = migrateDateFields(parsed.formData)
            setFormData(migratedData)
            setCurrentStep(parsed.currentStep)
            setCompletedSteps(parsed.completedSteps)
            toast.custom((t) => (
              <EnterpriseToast
                t={t}
                type="success"
                title="Draft Recovered"
                message="Your previous draft has been restored"
              />
            ))
            console.log('[EventWizard] Draft loaded from localStorage')
          } else {
            // User chose to start fresh
            localStorage.removeItem('event_draft')
            toast.custom((t) => (
              <EnterpriseToast
                t={t}
                type="success"
                title="Starting Fresh"
                message="Creating a new event"
              />
            ))
          }
        } catch (error) {
          console.error('Failed to load draft:', error)
          localStorage.removeItem('event_draft') // Remove corrupted data
          toast.custom((t) => (
            <EnterpriseToast
              t={t}
              type="error"
              title="Load Failed"
              message="Could not load previous draft. Starting fresh"
            />
          ))
        }
      }
    }
  }, [draftId])

  // Auto-save to localStorage
  useEffect(() => {
    const saveInterval = setInterval(() => {
      localStorage.setItem('event_draft', JSON.stringify({
        formData,
        currentStep,
        completedSteps,
        savedAt: new Date().toISOString()
      }))
    }, 30000) // Auto-save every 30 seconds

    return () => clearInterval(saveInterval)
  }, [formData, currentStep, completedSteps])

  const loadDraft = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('secret_events')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      // Load menu items
      const { data: menuItems, error: menuError } = await supabase
        .from('secret_menu_items')
        .select('*')
        .eq('event_id', id)
        .order('order_index')

      if (menuError) throw menuError

      console.log('[EventCreationWizard] Fetched Event Data from DB:', {
        registration_opens: data.registration_opens,
        pickup_start_time: data.pickup_start_time,
        pickup_end_time: data.pickup_end_time,
        event_date: data.event_date
      })

      // Helper to convert UTC datetime strings to local datetime format for form inputs
      const convertToLocalDateTime = (utcString: string | null) => {
        if (!utcString) return null

        const date = new Date(utcString)
        if (isNaN(date.getTime())) return utcString

        // Format as local datetime string without timezone suffix
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const seconds = String(date.getSeconds()).padStart(2, '0')

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
      }

      // Convert all datetime fields to local time for editing
      const localizedData = {
        ...data,
        notification_date: convertToLocalDateTime(data.notification_date),
        registration_opens: convertToLocalDateTime(data.registration_opens),
        registration_closes: convertToLocalDateTime(data.registration_closes),
        event_date: convertToLocalDateTime(data.event_date),
        pickup_start_time: convertToLocalDateTime(data.pickup_start_time),
        pickup_end_time: convertToLocalDateTime(data.pickup_end_time)
      }

      console.log('[EventCreationWizard] Converted to local times:', {
        registration_opens: localizedData.registration_opens,
        pickup_start_time: localizedData.pickup_start_time,
        pickup_end_time: localizedData.pickup_end_time,
        event_date: localizedData.event_date
      })

      setFormData({
        ...formData,
        ...localizedData,
        menuItems: menuItems || []
      })
    } catch (error) {
      console.error('Failed to load draft:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Load Failed"
          message="Could not load event draft"
        />
      ))
    }
  }

  const updateFormData = (updates: Partial<EventFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }

  const markStepComplete = (step: number) => {
    if (!completedSteps.includes(step)) {
      setCompletedSteps(prev => [...prev, step])
    }
  }

  const goToStep = (step: number) => {
    if (step <= currentStep || completedSteps.includes(step - 1)) {
      setCurrentStep(step)
    }
  }

  const nextStep = () => {
    // Validate Step 1: Event Details
    if (currentStep === 1) {
      if (!formData.title.trim()) {
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="error"
            title="Title Required"
            message="Please enter an event title"
          />
        ))
        return
      }
      if (formData.title.length > 100) {
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="error"
            title="Title Too Long"
            message="Event title must be 100 characters or less"
          />
        ))
        return
      }
      if (!formData.image_url) {
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="error"
            title="Image Required"
            message="Please upload an event image"
          />
        ))
        return
      }
    }

    // Validate Step 3: Location & Schedule
    if (currentStep === 3) {
      if (!formData.location_name.trim()) {
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="error"
            title="Location Required"
            message="Please enter a location name"
          />
        ))
        return
      }
      if (!formData.address.trim()) {
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="error"
            title="Address Required"
            message="Please enter a street address"
          />
        ))
        return
      }
      if (!formData.zip_code.trim()) {
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="error"
            title="Zip Code Required"
            message="Please enter a zip code"
          />
        ))
        return
      }
      if (!formData.event_date) {
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="error"
            title="Date Required"
            message="Please select an event date"
          />
        ))
        return
      }
    }

    markStepComplete(currentStep)
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const getNextStepName = (): string => {
    const stepNames: Record<number, string> = {
      1: 'Menu & Products',
      2: 'Location & Schedule',
      3: 'Rules & Settings',
      4: 'Review & Publish'
    }
    return stepNames[currentStep] || 'Next Step'
  }

  const handlePublish = async () => {
    try {
      // Validate all steps are complete
      if (completedSteps.length < STEPS.length - 1) {
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="error"
            title="Steps Incomplete"
            message="Please complete all required steps before publishing"
          />
        ))
        return
      }

      // Final validation
      if (formData.menuItems.length === 0) {
        toast.custom((t) => (
          <EnterpriseToast
            t={t}
            type="error"
            title="Menu Required"
            message="Please add at least one menu item before publishing"
          />
        ))
        setCurrentStep(2) // Go to menu step
        return
      }

      const confirmed = window.confirm(
        'Publish Event?\n\n' +
        'This will make the event visible to users and allow them to place pre-orders.\n\n' +
        'Do you want to continue?'
      )

      if (!confirmed) return

      toast.loading('Publishing event...')

      // Create or update event
      // Helper function to convert empty strings to null
      const sanitizeValue = (value: any) => {
        if (value === '' || value === undefined) return null
        return value
      }

      // Format datetime for database storage - append timezone offset
      const formatForDatabase = (localDateTimeString: string | null) => {
        if (!localDateTimeString) return null

        // Check if it already has a timezone suffix at the END of the string
        // Valid timezone formats: +HH:MM, -HH:MM, or Z
        const hasTimezoneSuffix = /[+-]\d{2}:\d{2}$/.test(localDateTimeString) || localDateTimeString.endsWith('Z')

        if (hasTimezoneSuffix) {
          console.log('[EventCreationWizard] Already has timezone suffix:', localDateTimeString)
          return localDateTimeString
        }

        // Append timezone offset to tell PostgreSQL what timezone this is in
        const date = new Date(localDateTimeString)
        const offsetMinutes = -date.getTimezoneOffset()
        const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60)
        const offsetMins = Math.abs(offsetMinutes) % 60
        const offsetSign = offsetMinutes >= 0 ? '+' : '-'
        const offsetString = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`

        const withOffset = `${localDateTimeString}${offsetString}`
        console.log('[EventCreationWizard] Appending timezone offset:', {
          input: localDateTimeString,
          output: withOffset
        })

        return withOffset
      }

      // notification_date defaults to registration_opens if not set (NOT NULL constraint in DB)
      const getNotificationDate = () => {
        if (formData.notification_date && formData.notification_date !== '') {
          return formatForDatabase(formData.notification_date)
        }
        // Default to registration_opens if available, otherwise use event_date
        if (formData.registration_opens && formData.registration_opens !== '') {
          return formatForDatabase(formData.registration_opens)
        }
        // Fallback to event_date if registration_opens is also empty
        return formatForDatabase(formData.event_date)
      }

      const eventData = {
        title: formData.title,
        description: formData.description,
        image_url: formData.image_url,
        location_name: formData.location_name,
        address: formData.address,
        zip_code: formData.zip_code,
        latitude: formData.latitude || 0,
        longitude: formData.longitude || 0,
        event_date: formatForDatabase(sanitizeValue(formData.event_date)),
        notification_date: getNotificationDate(),
        registration_opens: formatForDatabase(sanitizeValue(formData.registration_opens)),
        registration_closes: formatForDatabase(sanitizeValue(formData.registration_closes)),
        pickup_start_time: formatForDatabase(sanitizeValue(formData.pickup_start_time)),
        pickup_end_time: formatForDatabase(sanitizeValue(formData.pickup_end_time)),
        status: formData.status,
        max_orders: sanitizeValue(formData.max_orders),
        pre_order_limit: sanitizeValue(formData.pre_order_limit),
        max_lobster_tails: sanitizeValue(formData.max_lobster_tails),
        max_tails_per_order: sanitizeValue(formData.max_tails_per_order),
        created_by: user?.id,
        current_orders: 0
      }

      console.log('[EventCreationWizard] Submitting Event Payload:', {
        registration_opens: eventData.registration_opens,
        pickup_start_time: eventData.pickup_start_time,
        pickup_end_time: eventData.pickup_end_time,
        event_date: eventData.event_date
      })

      let eventId = draftId

      if (draftId) {
        // Update existing draft
        const { error } = await supabase
          .from('secret_events')
          .update(eventData)
          .eq('id', draftId)

        if (error) throw error
      } else {
        // Create new event
        const { data, error } = await supabase
          .from('secret_events')
          .insert(eventData)
          .select()
          .single()

        if (error) throw error
        eventId = data.id
      }

      // Save menu items
      if (formData.menuItems.length > 0) {
        // Delete existing menu items if updating
        if (draftId) {
          console.log('[EventWizard] Deleting existing menu items for event:', draftId)
          const { error: deleteError } = await supabase
            .from('secret_menu_items')
            .delete()
            .eq('event_id', draftId)

          if (deleteError) {
            console.error('[EventWizard] Failed to delete existing menu items:', deleteError)
            throw new Error(`Failed to delete existing menu items: ${deleteError.message}`)
          }
          console.log('[EventWizard] Successfully deleted existing menu items')
        }

        // Insert new menu items
        const menuItemsData = formData.menuItems.map(item => ({
          event_id: eventId,
          name: item.name,
          description: item.description,
          price: item.price,
          image_url: item.image_url,
          available_quantity: item.inventory,
          order_index: item.order_index, // Migration renames sort_order to order_index
          lobster_tail_qty: item.lobster_tail_qty || 0
        }))

        const { error: menuError } = await supabase
          .from('secret_menu_items')
          .insert(menuItemsData)

        if (menuError) throw menuError
      }

      // Clear auto-save
      localStorage.removeItem('event_draft')

      toast.dismiss() // Dismiss loading toast
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title={formData.status === 'scheduled' ? 'Event Published Successfully!' : 'Event Saved as Draft'}
          message={formData.status === 'scheduled' ? 'Customers can now view and place pre-orders.' : 'You can publish it later from the events list.'}
        />
      ))
      onSuccess?.(eventData)
    } catch (error) {
      console.error('Failed to publish event:', error)
      toast.dismiss()
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Failed to Publish Event"
          message="Please check your connection and try again. If the problem persists, contact support."
        />
      ))
    }
  }

  const CurrentStepComponent = STEPS[currentStep - 1].component

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header with Clear Draft and Auto-save info */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h1 className="menu-item-name text-2xl font-bold">Create New Event</h1>
          <span className="menu-item-description text-sm px-3 py-1 bg-muted rounded-full">
            Auto-saving
          </span>
        </div>
        {!draftId && (
          <button
            type="button"
            onClick={clearDraft}
            className="menu-item-name px-4 py-2 border border-border rounded-md hover:border-destructive hover:text-destructive transition-colors text-sm font-medium"
          >
            Clear Draft & Start Fresh
          </button>
        )}
      </div>

      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <button
                onClick={() => goToStep(step.id)}
                disabled={step.id > currentStep && !completedSteps.includes(step.id - 1)}
                className={`flex items-center space-x-2 ${
                  step.id === currentStep
                    ? 'text-primary font-semibold'
                    : completedSteps.includes(step.id)
                    ? 'text-foreground cursor-pointer hover:text-primary'
                    : 'text-muted-foreground cursor-not-allowed'
                }`}
              >
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                    step.id === currentStep
                      ? 'border-primary bg-primary text-primary-foreground'
                      : completedSteps.includes(step.id)
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-muted bg-background'
                  }`}
                >
                  {completedSteps.includes(step.id) ? (
                    <Check size={16} />
                  ) : (
                    <span className="text-sm">{step.id}</span>
                  )}
                </div>
                <span className="hidden md:inline text-sm">{step.id} {step.name}</span>
              </button>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${
                  completedSteps.includes(step.id) ? 'bg-primary' : 'bg-muted'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-card border border-border rounded-lg p-8 mb-6">
        <CurrentStepComponent
          formData={formData}
          updateFormData={updateFormData}
          onNext={nextStep}
          onPrev={prevStep}
          isFirstStep={currentStep === 1}
          isLastStep={currentStep === STEPS.length}
          onPublish={handlePublish}
        />
      </div>

      {/* Navigation */}
      <div className="sticky bottom-0 bg-card border-t border-border py-4 px-6 -mx-8 -mb-8 flex items-center justify-between shadow-lg">
        <button
          onClick={() => {
            if (window.confirm('Cancel Event Creation?\n\nAny unsaved changes will be lost.')) {
              onCancel?.()
            }
          }}
          className="menu-item-name px-6 py-2 border border-border rounded-md hover:bg-muted transition-colors font-medium"
        >
          Cancel
        </button>

        <div className="flex items-center space-x-3">
          {currentStep > 1 && (
            <button
              onClick={prevStep}
              className="menu-item-name flex items-center space-x-2 px-6 py-2 border border-border rounded-md hover:bg-muted transition-colors font-medium"
            >
              <ChevronLeft size={16} />
              <span>Previous</span>
            </button>
          )}

          <button
            onClick={currentStep === STEPS.length ? handlePublish : nextStep}
            className="flex items-center space-x-2 px-8 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors font-semibold shadow-sm"
          >
            <span>
              {currentStep === STEPS.length
                ? 'Publish Event'
                : `Next: ${getNextStepName()}`
              }
            </span>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default EventCreationWizard
